import { Stack, Flex, Input, Skeleton, Button, VStack } from '@chakra-ui/react'
import { CenteredSpinner } from '@components/Loader/CenteredSpinner'
import { FailedToLoad } from '@components/Placeholders/FailedToLoad'
import { OrgStack } from '@features/organisations/components/stack/OrgStack'
import { OrgStackItem } from '@features/organisations/components/stack/OrgStackItem'
import { useStaffListOrgsQuery } from '@features/staffMember/api/org'
import { CreateOrg } from '@features/staffMember/components/orgs/CreateOrg'
import { ListOrgsRightContent } from '@features/staffMember/components/orgs/ListOrgsRightContent'
import { StaffOrgDetails } from '@features/staffMember/types'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useSearch } from '@hooks/useSearch'
import { useEffect } from 'react'
import { Route as listSessionsRoute } from '@routes/_auth/o/$orgId/sessions'
import { SentryErrorComponent } from '@components/Fallback'
import { CopyId } from '@components/CopyID'

const StaffOrgListRoute = () => {
  const { data, isLoading, isFetching, isError } = useStaffListOrgsQuery(null)
  const { searchResults, setSearchStr, setSearchArray, isSearching } =
    useSearch<StaffOrgDetails>({
      defaultSearchKeys: ['displayName'],
    })

  useEffect(() => setSearchArray(data ?? []), [data, setSearchArray])

  if (isError) {
    return <FailedToLoad />
  }

  return (
    <Stack spacing={3}>
      <Flex justify={'space-between'}>
        <Input
          placeholder="Search"
          maxW={'24rem'}
          onChange={(e) => setSearchStr(e.target.value)}
        />
        <CreateOrg />
      </Flex>
      <Skeleton isLoaded={!isLoading} minH={'20rem'}>
        {data && (
          <OrgStack>
            {searchResults.map(({ displayName, id }) => (
              <OrgStackItem
                key={id}
                leftContent={
                  <VStack alignItems={'flex-start'}>
                    <Link to={listSessionsRoute.to} params={{ orgId: id }}>
                      <Button variant="link" colorScheme="blue" rounded="md">
                        {displayName}
                      </Button>
                    </Link>
                    <CopyId value={id} whiteSpace={'nowrap'} />
                  </VStack>
                }
                rightContent={
                  <ListOrgsRightContent orgId={id} name={displayName} />
                }
              />
            ))}
          </OrgStack>
        )}
        {(isFetching || isSearching) && <CenteredSpinner />}
      </Skeleton>
    </Stack>
  )
}

export const Route = createFileRoute('/_auth/staff/o/')({
  component: StaffOrgListRoute,
  beforeLoad: () => ({ getTitle: () => 'Organisations' }),
  errorComponent: SentryErrorComponent,
})
